import { FormikHelpers } from 'formik';
import { JobFormData } from '@/jobs/application/types/JobTypes';
import { useCreateJob } from '@/jobs/application/adapters/createJob';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { useUpdateJob } from '@/jobs/application/adapters/updateJob';
import { useCalendarStore } from '@/store/CalendarStore';
import { toDate } from '@/common/application/utils/timeUtils';
import { useFeatureFlag } from '@/common/presentation/hooks';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import { mapValidationErrorsToFormikErrors } from '@/jobs/application/utils/validationErrorMapper';
import { JobMutationError } from '@/jobs/application/types/JobMutationError';

export function useJobForm(isNew: boolean, onClose?: () => void) {
  const redirectToJobDetailsAfterAddEnabled = useFeatureFlag(FEATURE_FLAGS.JOBS_ADD_NEW_SHOW_DETAILS);
  const stayOnTabAfterEditEnabled = useFeatureFlag(FEATURE_FLAGS.JOBS_RESOURCE_ASSIGNMENT_STAY_ON_TAB);

  const navigate = useNavigate();
  const { handleCreateJob, error: createError } = useCreateJob();
  const { handleUpdateJob, error: updateError, loading: updateLoading } = useUpdateJob();
  const { showSnackbar, showErrorWithDetails } = useSnackbar();
  const setStartDate = useCalendarStore((s: { setStartDate: (date: Date) => void }) => s.setStartDate);
  const setJobId = useCalendarStore((s: { setJobId: (id: number) => void }) => s.setJobId);

  const handleSubmit = async (
    values: JobFormData,
    { setSubmitting, resetForm, setFieldError, setFieldTouched }: FormikHelpers<JobFormData>
  ) => {
    let redirectToDetailsPage = false;

    try {
      showSnackbar('Info', 'Saving job...', 'info');
      if (isNew) {
        const result = await handleCreateJob(values);
        showSnackbar('Success', 'Job created successfully', 'success');

        const jobId = result?.data?.createRaptorJob?.value?.id;
        redirectToDetailsPage = Boolean(redirectToJobDetailsAfterAddEnabled && jobId && !onClose);
        if (redirectToDetailsPage) {
          navigate(`/jobs/${jobId}`);
          resetForm();
          return; // Early return to avoid the navigation logic below
        }
      } else {
        if (!values.id) throw new Error('Job ID is missing');
        await handleUpdateJob(values);
        showSnackbar('Success', 'Job updated successfully', 'success');
      }

      if (onClose) {
        onClose();
      } else {
        // save needed variables in zustand for later
        const d = toDate(values.startDate);
        if (d) setStartDate(d);
        setJobId(values.id!);
        if (!stayOnTabAfterEditEnabled || (isNew && !redirectToDetailsPage)) {
          navigate(-1);
        }
      }
      resetForm();
    } catch (error: unknown) {
      // Check if error has validation errors that need to be mapped to Formik fields
      const jobError = error as JobMutationError;

      if (jobError?.validationErrors && jobError.validationErrors.length > 0) {
        // Map backend validation errors to Formik field errors
        const formikErrors = mapValidationErrorsToFormikErrors(jobError.validationErrors);

        // Set errors on Formik fields and mark them as touched so error messages are displayed
        for (const [fieldName, errorMessage] of Object.entries(formikErrors)) {
          setFieldError(fieldName, errorMessage);
          setFieldTouched(fieldName, true, false); // Mark field as touched without triggering validation
        }
      }

      const errorTitle = isNew ? 'Job Creation Failed' : 'Job Update Failed';
      showErrorWithDetails(error, 'Failed to save job', errorTitle);
    } finally {
      setSubmitting(false);
    }
  };

  return { handleSubmit, createError, updateError, updateLoading };
}
